import React from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';           // ← Added this
import { fetchSettlementRequests } from 'services/apiSrv';
import { formatDate } from 'utils/helpers';

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacOSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 32px;
  text-align: center;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  border: 1px solid #e5e7eb;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 20px;
  padding: 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  text-align: center;
`;

const SummaryLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 6px;
`;

const SummaryValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
`;

const Th = styled.th`
  background: #f1f5f9;
  padding: 14px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
  vertical-align: middle;
`;

const StatusBadge = styled.span`
  padding: 5px 12px;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 600;
  display: inline-block;
  text-transform: capitalize;

  ${({ $status }) => {
    switch ($status?.toLowerCase()) {
      case 'processing':
      case 'pending':
        return 'background: #fef3c7; color: #92400e;';
      case 'completed':
      case 'success':
        return 'background: #d1fae5; color: #065f46;';
      case 'failed':
      case 'rejected':
      case 'cancelled':
        return 'background: #fee2e2; color: #991b1b;';
      default:
        return 'background: #e5e7eb; color: #4b5563;';
    }
  }}
`;

const ActionLink = styled(Link)`
  background: none;
  border: none;
  color: #2563eb;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #1d4ed8;
  }
`;


const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #6b7280;
  font-size: 1.1rem;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
  gap: 8px;
  color: #4b5563;
  font-size: 0.95rem;
`;

function SettlementRequests() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['settlement-requests'],
    queryFn: async () => {
      const res = await fetchSettlementRequests();
      return res.data || [];
    },
  });

  // For demo/summary - in real app you'd calculate from real data
  const summary = {
    processing: 50000,
    failed: 0,
    totalThisWeek: data?.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) ?? 0,
  };

  const requests = data ?? [];

  return (
    <Container>
      <Title>Settlement Requests</Title>

      <Card>
        {/* Summary Section */}
        <SummaryGrid>
          <SummaryCard>
            <SummaryLabel>Processing</SummaryLabel>
            <SummaryValue>KES {summary.processing.toLocaleString()}</SummaryValue>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>Failed</SummaryLabel>
            <SummaryValue>KES {summary.failed.toLocaleString()}</SummaryValue>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>This Week</SummaryLabel>
            <SummaryValue>KES {summary.totalThisWeek.toLocaleString()}</SummaryValue>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>Total Requests</SummaryLabel>
            <SummaryValue>{requests.length}</SummaryValue>
          </SummaryCard>
        </SummaryGrid>

        {/* Table */}
        {isLoading ? (
          <EmptyState>Loading settlement requests...</EmptyState>
        ) : error ? (
          <EmptyState>Error loading data. Please try again later.</EmptyState>
        ) : requests.length === 0 ? (
          <EmptyState>No settlement requests found.</EmptyState>
        ) : (
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Amount (KES)</Th>
                  <Th>Status</Th>
                  <Th>Remarks</Th>
                  <Th>Channel</Th>
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req, index) => (
                  <tr key={req.id || index}>
                    <Td>{formatDate(req.createdAt)}</Td>
                    <Td>{Number(req.amount || 0).toLocaleString()}</Td>
                    <Td>
                      <StatusBadge $status={req.settlementStatus}>
                        {req.settlementStatus || 'Unknown'}
                      </StatusBadge>
                    </Td>
                    <Td>{req.settlementRemarks || '—'}</Td>
                    <Td>{req.settlementChannel || '—'}</Td> {/* fixed typo: settlementChannnel → settlementChannel */}
                    <Td>
                      <ActionLink to={`/dashboard/settlements/requests/${req.settlementRequestSystemRef}`}>
                        View
                      </ActionLink>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}

      </Card>
    </Container>
  );
}

export default SettlementRequests;